import { formatDistanceToNow, format } from 'date-fns'
import type { RawContact, RawMessage } from '../components/types'

// Human-readable channel label for a message. Mirrors moderation's ContactList:
// the top-level `channel` is the transport; for telerivet the finer sub-channel
// (WhatsApp / Instagram / FB Messenger) lives in integration.route_name.
export function getMessageChannelLabel(message: Pick<RawMessage, 'channel' | 'integration'>): string {
  const channel = message.channel
  if (!channel) return 'Unknown'

  if (channel === 'telerivet') {
    const integration = (message.integration || {}) as Record<string, unknown>
    const routeNameRaw = typeof integration.route_name === 'string' ? integration.route_name : undefined
    if (routeNameRaw) {
      const cleaned = routeNameRaw.replace(/\s*<[^>]+>\s*$/, '').trim()
      if (cleaned) return cleaned
    }
    return 'Telerivet'
  }

  if (channel === 'app') return 'App'
  return channel.charAt(0).toUpperCase() + channel.slice(1)
}

export function formatTime(timestamp: number): string {
  if (!timestamp) return ''
  return formatDistanceToNow(new Date(timestamp * 1000), { addSuffix: true })
}

export function formatFullTime(timestamp: number): string {
  if (!timestamp) return ''
  return format(new Date(timestamp * 1000), 'MMM dd, yyyy HH:mm:ss')
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  // Past an hour, keep the smaller unit alongside the larger one: "1h 32m"
  // reads very differently from the "2h" a bare rounding would produce.
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.round((seconds % 3600) / 60)
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }
  const d = Math.floor(seconds / 86400)
  const h = Math.round((seconds % 86400) / 3600)
  return h > 0 ? `${d}d ${h}h` : `${d}d`
}

export function getDisplayName(contact: RawContact): string {
  return contact.name || contact.phone_number || 'Unknown'
}

export function getInitials(contact: RawContact): string {
  const name = getDisplayName(contact)
  return name.substring(0, 2).toUpperCase()
}

export function exportToCSV(
  data: Record<string, any>[],
  filename: string,
  columns?: string[]
) {
  if (data.length === 0) return

  const cols = columns || Object.keys(data[0])
  const csvContent = [
    cols.join(','),
    ...data.map(row =>
      cols.map(col => {
        const val = row[col]
        if (val === undefined || val === null) return ''
        const str = String(val)
        // Escape quotes and wrap in quotes if contains comma or quote
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }).join(',')
    )
  ].join('\n')

  // UTF-8 BOM so Excel (and other tools on Windows) detect UTF-8 and display accented chars correctly
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
